/**
 * L'haptique — le retour dans la main.
 *
 * Le § 3.1 la compte parmi les manques de la v1, à égalité avec le son. Elle dit
 * quelque chose que ni l'écran ni le haut-parleur ne disent : que ce qui vient de se
 * passer **te** concerne. C'est pour ça qu'elle est avare (voir `partition.ts`) — une
 * vibration qui arrive à chaque coup ne signale plus rien.
 *
 * ## Ce qui la déclenche, et où
 *
 * - **Web** : `navigator.vibrate`, disponible sur Android. Safari iOS ne l'implémente
 *   pas, et aucun contournement n'existe côté web : sur iPhone, la version navigateur
 *   est silencieuse de ce côté-là, et c'est assumé.
 * - **Natif** : `@capacitor/haptics` au lot 9, qui donne les vrais retours du moteur
 *   Taptic. Ce module est l'endroit unique à changer — rien d'autre n'appelle
 *   `navigator.vibrate`.
 *
 * ## Le respect du réglage système
 *
 * `prefers-reduced-motion` porte sur l'animation, pas sur l'haptique, et s'en servir
 * pour couper les vibrations serait un contresens. En revanche, le réglage de son du
 * jeu commande les deux : quelqu'un qui coupe le son dans un bar veut être discret,
 * pas seulement silencieux.
 */

/** Vrai si l'appareil sait vibrer. Faux sur iOS Safari et sur ordinateur. */
export function haptiqueDisponible(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
}

/**
 * Vibre selon un motif, en millisecondes (vibre, pause, vibre…).
 *
 * Ne lève jamais : certains navigateurs refusent la vibration hors geste utilisateur,
 * et une exception ici couperait la mise en scène pour un confort.
 */
export function vibrer(motif: readonly number[]): void {
  if (motif.length === 0 || !haptiqueDisponible()) return;
  try {
    navigator.vibrate([...motif]);
  } catch {
    // Refus du navigateur : tant pis, le son et l'écran disent déjà l'essentiel.
  }
}

/** Coupe une vibration en cours — au passage en arrière-plan, par exemple. */
export function couperHaptique(): void {
  if (!haptiqueDisponible()) return;
  try {
    navigator.vibrate(0);
  } catch {
    // Rien à faire de plus.
  }
}
